import React, { useEffect, useMemo, useState } from 'react';
import {
  Bar,
  CartesianGrid,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  ComposedChart,
} from 'recharts';
import GoToDashboardButton from './GoToDashboardButton';
import styles from './ProductionRootCause.module.css';
import SourceFileSummary from './SourceFileSummary';
import {
  artavaRechartsAxis,
  artavaRechartsGrid,
  artavaRechartsTooltip,
} from './chartPalette';
import { fetchCascadeSpreadAnalysis } from './productionRootCause/productionRootCauseApi';

const defaultFilters = {
  startDate: '',
  endDate: '',
  ateSwVersion: '',
  stationName: '',
  selectedFamilies: [],
};

const CascadeFailureSpread = () => {
  const [filters, setFilters] = useState(defaultFilters);
  const [data, setData] = useState({ summary: null, rows: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const topRows = useMemo(() => data.rows.slice(0, 12), [data.rows]);

  const loadData = async (nextFilters = filters) => {
    setLoading(true);
    setError('');
    try {
      const response = await fetchCascadeSpreadAnalysis(nextFilters);
      setData(response || { summary: null, rows: [] });
    } catch (requestError) {
      setError(requestError.message || 'Failed to load cascade spread analysis.');
      setData({ summary: null, rows: [] });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(defaultFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateFilter = (key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const resetFilters = () => {
    setFilters(defaultFilters);
    loadData(defaultFilters);
  };

  const summary = data.summary || {};

  return (
    <div className={styles.pageContainer}>
      <GoToDashboardButton />

      <header className={styles.pageHeader}>
        <div>
          <p className={styles.eyebrow}>Root problem idea 4</p>
          <h1 className={styles.title}>Cascade Failure Spread</h1>
          <p className={styles.description}>
            Measure how much damage a first failure tends to create after it appears. This page starts from the first failed step in each failed session, then counts how many additional fail steps follow.
          </p>
        </div>
      </header>

      <section className={styles.filterPanel}>
        <div className={styles.filterHeader}>
          <div>
            <h3>Filters</h3>
            <p>Use this when you want to separate isolated failures from failure triggers that cause long cascades across the rest of the test flow.</p>
          </div>
        </div>
        <div className={styles.filterGrid}>
          <div className={styles.filterItem}>
            <label>Start date</label>
            <input type="date" value={filters.startDate} onChange={(event) => updateFilter('startDate', event.target.value)} />
          </div>
          <div className={styles.filterItem}>
            <label>End date</label>
            <input type="date" value={filters.endDate} onChange={(event) => updateFilter('endDate', event.target.value)} />
          </div>
          <div className={styles.filterItem}>
            <label>ATE SW version</label>
            <input value={filters.ateSwVersion} onChange={(event) => updateFilter('ateSwVersion', event.target.value)} placeholder="All versions" />
          </div>
          <div className={styles.filterItem}>
            <label>Station name</label>
            <input value={filters.stationName} onChange={(event) => updateFilter('stationName', event.target.value)} placeholder="All stations" />
          </div>
          <div className={styles.filterActions}>
            <button className={styles.primaryButton} onClick={() => loadData()} disabled={loading}>Run analysis</button>
            <button className={styles.secondaryButton} onClick={resetFilters}>Reset</button>
          </div>
        </div>
        <SourceFileSummary
          compact
          startDate={filters.startDate}
          endDate={filters.endDate}
          ateSwVersion={filters.ateSwVersion}
          selectedFamilies={filters.selectedFamilies}
          onFamiliesChange={(values) => updateFilter('selectedFamilies', values)}
          title="Source files used for dashboard charts"
        />
      </section>

      {error && <div className={styles.error}>{error}</div>}

      <section className={styles.metricGrid}>
        <div className={styles.metricCard}><span>Failed sessions</span><strong>{summary.failedSessions || 0}</strong></div>
        <div className={styles.metricCard}><span>First-fail patterns</span><strong>{summary.firstFailPatterns || 0}</strong></div>
        <div className={styles.metricCard}><span>Top cascade score</span><strong>{summary.topCascadeScore || 0}</strong></div>
        <div className={styles.metricCard}><span>Top avg downstream fails</span><strong>{summary.topAvgDownstreamFails || 0}</strong></div>
      </section>

      {loading ? (
        <div className={styles.loading}>Measuring cascade spread...</div>
      ) : data.rows.length === 0 ? (
        <div className={styles.emptyState}>No cascade patterns matched this slice.</div>
      ) : (
        <>
          <section className={styles.chartPanel}>
            <div className={styles.panelHeader}>
              <div>
                <h3>Downstream fail spread</h3>
                <p>Bars show how many fail steps tend to follow the first failure. The line shows the share of sessions where that first failure leads to a heavy cascade.</p>
              </div>
            </div>
            <div className={styles.chartBox}>
              <ResponsiveContainer>
                <ComposedChart data={topRows} margin={{ top: 10, right: 20, bottom: 45, left: 0 }}>
                  <CartesianGrid {...artavaRechartsGrid} vertical={false} />
                  <XAxis dataKey="stepNumber" angle={-35} textAnchor="end" height={75} {...artavaRechartsAxis} />
                  <YAxis yAxisId="left" {...artavaRechartsAxis} />
                  <YAxis yAxisId="right" orientation="right" {...artavaRechartsAxis} />
                  <Tooltip {...artavaRechartsTooltip} />
                  <Bar yAxisId="left" dataKey="avgDownstreamFails" name="Avg downstream fails" fill="#8a3f5d" radius={[6, 6, 0, 0]} />
                  <Line yAxisId="right" type="monotone" dataKey="cascadeHeavyRate" name="Cascade-heavy %" stroke="#24745f" strokeWidth={3} dot={{ r: 4, fill: '#24745f' }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </section>

          <section className={styles.insightGrid}>
            {data.rows.slice(0, 3).map((row) => (
              <article className={styles.insightCard} key={row.stepNumber}>
                <p className={styles.eyebrow}>Cascade trigger</p>
                <h3>{row.stepNumber} - {row.stepName}</h3>
                <p>After this step fails first, it leads to {row.avgDownstreamFails} extra fail steps on average and a heavy cascade in {row.cascadeHeavyRate}% of sessions.</p>
                <div className={styles.chips}>
                  <span>{row.affectedSerials} serials</span>
                  <span>{row.avgDurationMinutes} min avg</span>
                </div>
              </article>
            ))}
          </section>

          <section className={styles.tablePanel}>
            <div className={styles.panelHeader}>
              <div>
                <h3>Cascade evidence</h3>
                <p>Use this page to prioritize fixes that remove long chains of downstream failures instead of only reducing fail counts at a single step.</p>
              </div>
            </div>
            <table className={styles.dataTable}>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Step</th>
                  <th>Sessions</th>
                  <th>Avg downstream</th>
                  <th>Avg total fails</th>
                  <th>Cascade-heavy</th>
                  <th>Serials</th>
                  <th>Avg duration</th>
                  <th>Score</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row, index) => (
                  <tr key={row.stepNumber}>
                    <td>{index + 1}</td>
                    <td className={styles.stepName}>{row.stepNumber}<br /><span className={styles.muted}>{row.stepName}</span></td>
                    <td>{row.sessions}</td>
                    <td>{row.avgDownstreamFails}</td>
                    <td>{row.avgTotalFailSteps}</td>
                    <td>{row.cascadeHeavyRate}%</td>
                    <td>{row.affectedSerials}</td>
                    <td>{row.avgDurationMinutes} min</td>
                    <td>{row.score}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </>
      )}
    </div>
  );
};

export default CascadeFailureSpread;
